
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Navbar } from './components/Navbar';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { Footer } from './components/Footer';
import { RouterProvider, useRouter } from './lib/router';
import { DashboardProvider, useDashboard } from './lib/DashboardContext';
import { ServerTimeProvider } from './lib/ServerTimeContext';
import PresetsModal from './components/PresetsModal';
import { DashboardGuard } from './components/DashboardGuard';
import { addPreset } from './lib/presets';
import { useSessionGuard } from './lib/sessionGuard';
import { useSeo } from './lib/seo';
import { ConsentBanner } from './components/ConsentBanner';
import { trackMeta, hasConsent, clearPartnerPixel } from './lib/metaTracking';

// Lazy-load pages to enable route-based code-splitting and faster first paint
const Home = lazy(() => import('./pages/Home').then(m => ({ default: m.Home })));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage').then(m => ({ default: m.FeaturesPage })));
const VisionPage = lazy(() => import('./pages/VisionPage').then(m => ({ default: m.VisionPage })));
const PartnerPage = lazy(() => import('./pages/PartnerPage').then(m => ({ default: m.PartnerPage })));
const DownloadPage = lazy(() => import('./pages/DownloadPage').then(m => ({ default: m.DownloadPage })));
const DriverLogin = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.DriverLogin })));
const OrganizerLogin = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.OrganizerLogin })));
const BarLogin = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.BarLogin })));
const ClubLogin = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.ClubLogin })));
const FleetLogin = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.FleetLogin })));
const MasterLogin = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.MasterLogin })));

const Impressum = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.Impressum })));
const Datenschutz = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.Datenschutz })));
const AGB = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.AGB })));
const LegalOverview = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.LegalOverview })));
const Jugendschutz = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.Jugendschutz })));
const Widerruf = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.Widerruf })));
const Haftung = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.Haftung })));

const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const CitiesPage = lazy(() => import('./pages/ContentPages').then(m => ({ default: m.CitiesPage })));
const CareersPage = lazy(() => import('./pages/ContentPages').then(m => ({ default: m.CareersPage })));
const OrganizerInfoPage = lazy(() => import('./pages/BusinessPages').then(m => ({ default: m.OrganizerInfoPage })));
const BarModelPage = lazy(() => import('./pages/BusinessPages').then(m => ({ default: m.BarModelPage })));
const CampaignsPage = lazy(() => import('./pages/CampaignsPage').then(m => ({ default: m.CampaignsPage })));
const EventsPage = lazy(() => import('./pages/EventsPage').then(m => ({ default: m.EventsPage })));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage').then(m => ({ default: m.EventDetailPage })));
const DealDetailPage = lazy(() => import('./pages/DealDetailPage').then(m => ({ default: m.DealDetailPage })));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const FAQPage = lazy(() => import('./pages/FAQPage').then(m => ({ default: m.FAQPage })));
const PulsePage = lazy(() => import('./pages/PulsePage').then(m => ({ default: m.PulsePage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));

// Dashboards
const ClubDashboard = lazy(() => import('./pages/dashboards/ClubDashboard').then(m => ({ default: m.ClubDashboard })));
const OrganizerDashboard = lazy(() => import('./pages/dashboards/OrganizerDashboard').then(m => ({ default: m.OrganizerDashboard })));
const BarDashboard = lazy(() => import('./pages/dashboards/BarDashboard').then(m => ({ default: m.BarDashboard })));
const DriverDashboard = lazy(() => import('./pages/dashboards/DriverDashboard').then(m => ({ default: m.DriverDashboard })));
const FleetDashboard = lazy(() => import('./pages/dashboards/FleetDashboard').then(m => ({ default: m.FleetDashboard })));
const MasterDashboard = lazy(() => import('./pages/dashboards/MasterDashboard').then(m => ({ default: m.MasterDashboard })));
const CustomerDashboard = lazy(() => import('./pages/dashboards/CustomerDashboard').then(m => ({ default: m.default })));

// Auth / account pages
const GuestLogin = lazy(() => import('./pages/GuestLogin').then(m => ({ default: m.default })));
const GuestRegister = lazy(() => import('./pages/GuestRegister').then(m => ({ default: m.default })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.default })));
const AccountTickets = lazy(() => import('./pages/account/Tickets').then(m => ({ default: m.default })));
const AccountProfile = lazy(() => import('./pages/account/Profile').then(m => ({ default: m.default })));

const DebugFileInputContent: React.FC = () => {
  const { path } = useRouter();
  try { console.log('[App] current path:', path); } catch (e) {}

  const [status, setStatus] = useState('Noch keine Datei gewählt');
  const [preview, setPreview] = useState('');
  const [fileName, setFileName] = useState('');
  const [dropStatus, setDropStatus] = useState('Noch kein Drop getestet');
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const previewRef = React.useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const onChange = () => {
      const file = input.files && input.files[0] ? input.files[0] : null;
      console.log('[debug-file-input] native change', file ? { name: file.name, type: file.type, size: file.size } : null);
      if (!file) {
        setStatus('change ohne Datei');
        return;
      }
      setFileName(file.name);
      setStatus(`Datei gewählt: ${file.name}`);
      const url = URL.createObjectURL(file);
      setPreview(prev => {
        if (prev) {
          try { URL.revokeObjectURL(prev); } catch (e) {}
        }
        return url;
      });
    };

    const onInput = () => {
      const file = input.files && input.files[0] ? input.files[0] : null;
      console.log('[debug-file-input] native input', file ? { name: file.name, type: file.type, size: file.size } : null);
      setStatus(file ? `input: ${file.name}` : 'input ohne Datei');
    };

    const onDrop = (event: DragEvent) => {
      event.preventDefault();
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0] ? event.dataTransfer.files[0] : null;
      console.log('[debug-file-input] native drop', file ? { name: file.name, type: file.type, size: file.size } : null);
      if (!file) {
        setDropStatus('Drop ohne Datei');
        return;
      }
      setDropStatus(`Drop: ${file.name}`);
      setFileName(file.name);
      setStatus(`Drop-Datei: ${file.name}`);
      const url = URL.createObjectURL(file);
      setPreview(prev => {
        if (prev) {
          try { URL.revokeObjectURL(prev); } catch (e) {}
        }
        return url;
      });
    };

    const onDragOver = (event: DragEvent) => {
      event.preventDefault();
    };

    input.addEventListener('change', onChange);
    input.addEventListener('input', onInput);
    window.addEventListener('drop', onDrop);
    window.addEventListener('dragover', onDragOver);
    return () => {
      input.removeEventListener('change', onChange);
      input.removeEventListener('input', onInput);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragover', onDragOver);
      if (previewRef.current) {
        try { URL.revokeObjectURL(previewRef.current.src); } catch (e) {}
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="mx-auto max-w-2xl rounded-3xl border border-white/10 bg-white/5 p-6">
        <div className="mb-4 text-2xl font-bold">Debug File Input</div>
        <div className="mb-2 text-sm text-gray-400">{status}</div>
        <div className="mb-4 text-sm text-cyan-300">{dropStatus}</div>
        <label htmlFor="debug-file-input-native" className="mb-2 block text-sm text-gray-300">
          Datei per Finder auswählen oder per Drag & Drop in die Box legen
        </label>
        <input
          ref={inputRef}
          id="debug-file-input-native"
          type="file"
          accept="image/*"
          className="mb-4 block w-full cursor-pointer rounded-xl border border-white/15 bg-black/30 px-4 py-3 text-sm text-gray-200 file:mr-4 file:rounded-full file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-semibold file:text-black hover:file:bg-gray-200"
          onChange={(e) => {
            const file = e.currentTarget.files && e.currentTarget.files[0] ? e.currentTarget.files[0] : null;
            console.log('[debug-file-input] react change', file ? { name: file.name, type: file.type, size: file.size } : null);
            if (!file) return;
            setFileName(file.name);
            setStatus(`React change: ${file.name}`);
            const url = URL.createObjectURL(file);
            setPreview(prev => {
              if (prev) {
                try { URL.revokeObjectURL(prev); } catch (err) {}
              }
              return url;
            });
          }}
          onInput={(e) => {
            const file = e.currentTarget.files && e.currentTarget.files[0] ? e.currentTarget.files[0] : null;
            console.log('[debug-file-input] react input', file ? { name: file.name, type: file.type, size: file.size } : null);
            if (file) setStatus(`React input: ${file.name}`);
          }}
          onClick={(e) => {
            console.log('[debug-file-input] input clicked');
          }}
        />
        <div className="mb-4 text-xs text-cyan-300 break-all">{fileName || 'Keine Datei'}</div>
        <div className="min-h-56 rounded-2xl border border-white/10 bg-black/40 flex items-center justify-center overflow-hidden">
          {preview ? <img ref={previewRef} src={preview} alt={fileName || 'preview'} className="w-full h-56 object-cover" /> : <div className="text-sm text-gray-500">Noch keine Vorschau.</div>}
        </div>
      </div>
    </div>
  );
};

const AppContent: React.FC = () => {
  const { path } = useRouter();
  const { addNotification } = useDashboard();

  // Single-active-session guard: if another tab logs in as a different account
  // (any dashboard), this tab is logged out automatically.
  useSessionGuard();

  // Debug: log routing decisions
  try { console.log('[App] current path:', path); } catch (e) {}

  // Debug overlay removed: do not track debugPresetsOpen in production flows
  const [topPresetsOpen, setTopPresetsOpen] = useState(false);
  const [dummyTemplateOpen, setDummyTemplateOpen] = useState(false);
  const [globalSaveTemplateOpen, setGlobalSaveTemplateOpen] = useState(false);
  const [globalSaveTemplateData, setGlobalSaveTemplateData] = useState<any>(null);
  const [globalSaveTemplateName, setGlobalSaveTemplateName] = useState<string>('');

  useEffect(() => {
    const handler = (e: any) => {
      try {
        const el = e.target as HTMLElement | null;
        if (!el) return;
        const btn = el.closest && el.closest('button');
        if (!btn) return;
        const txt = (btn.textContent || '').trim();
        const watch = ['Template laden', 'Template speichern', 'Vorlage speichern', 'Vorlage laden', 'Preset', 'Hinzufügen'];
        if (watch.some(w => txt.includes(w))) {
          console.log('GlobalTemplateClick detected', { text: txt, node: btn });
        }
      } catch (err) {
        // ignore
      }
    };
    document.addEventListener('click', handler, true);
    // legacy debug events are ignored in this build
    const openTop = (ev?: any) => { try { console.log('App: openPresetsModal received', ev); } catch (e) {} setTopPresetsOpen(true); };
    const closeTop = (ev?: any) => { try { console.log('App: closePresetsModal received', ev); } catch (e) {} setTopPresetsOpen(false); };
    const openDummy = (ev?: any) => { try { console.log('App: openDummyTemplatePage received', ev); } catch (e) {} setDummyTemplateOpen(true); };
    const closeDummy = (ev?: any) => { try { console.log('App: closeDummyTemplatePage received', ev); } catch (e) {} setDummyTemplateOpen(false); };
    const openGlobalSave = (ev?: any) => { try { console.log('App: openSaveTemplateModal received', ev); setGlobalSaveTemplateData(ev && ev.detail ? ev.detail : null); setGlobalSaveTemplateName(ev && ev.detail && ev.detail.name ? ev.detail.name : ''); } catch (e) { setGlobalSaveTemplateData(null); setGlobalSaveTemplateName(''); } setGlobalSaveTemplateOpen(true); };
    const closeGlobalSave = (ev?: any) => { try { console.log('App: closeSaveTemplateModal received', ev); } catch (e) {} setGlobalSaveTemplateOpen(false); setGlobalSaveTemplateData(null); setGlobalSaveTemplateName(''); };
    // removed debug event listeners
    document.addEventListener('openPresetsModal', openTop as EventListener);
    document.addEventListener('closePresetsModal', closeTop as EventListener);
    document.addEventListener('openDummyTemplatePage', openDummy as EventListener);
    document.addEventListener('closeDummyTemplatePage', closeDummy as EventListener);
    document.addEventListener('openSaveTemplateModal', openGlobalSave as EventListener);
    document.addEventListener('closeSaveTemplateModal', closeGlobalSave as EventListener);
    const saveHandler = async (ev?: any) => {
      try {
        console.log('App: saveTemplateConfirmed received', ev && ev.detail ? ev.detail : null);
        const detail = ev && ev.detail ? ev.detail : null;
        if (!detail) return;
        const accountId = typeof window !== 'undefined' ? (sessionStorage.getItem('activeAccountId') || localStorage.getItem('accountId')) : null;
        // persist preset locally
          try {
          const preset = { id: Date.now().toString(), name: detail.name || `Vorlage ${new Date().toLocaleString()}`, cats: detail.categories || [], capacity: detail.capacity ?? undefined };
          try { addPreset(preset as any, accountId); } catch (e) { console.warn('addPreset failed', e); }
          console.log('App: saved preset via addPreset', preset);
        } catch (e) { console.warn('save preset local failed', e); }

        // if eventId provided, call server API to persist categories
        if (detail.eventId) {
          try {
            const body = { categories: detail.categories || [], ticketingType: 'categories', organizerId: accountId };
            const res = await fetch(`/api/events/${encodeURIComponent(detail.eventId)}/categories`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
            const j = await res.json();
            console.log('App: server save response', j);
            try { addNotification && addNotification('Vorlage und Kategorien gespeichert'); } catch (e) { /* ignore */ }
          } catch (e) { console.error('App: server save failed', e); }
        }
      } catch (e) { console.error('saveHandler error', e); }
    };
    document.addEventListener('saveTemplateConfirmed', saveHandler as EventListener);
    return () => {
      document.removeEventListener('click', handler, true);
      // cleanup for debug listeners removed
      document.removeEventListener('openPresetsModal', openTop as EventListener);
      document.removeEventListener('closePresetsModal', closeTop as EventListener);
      document.removeEventListener('openDummyTemplatePage', openDummy as EventListener);
      document.removeEventListener('closeDummyTemplatePage', closeDummy as EventListener);
      document.removeEventListener('openSaveTemplateModal', openGlobalSave as EventListener);
      document.removeEventListener('closeSaveTemplateModal', closeGlobalSave as EventListener);
      document.removeEventListener('saveTemplateConfirmed', saveHandler as EventListener);
    };
  }, []);

  // Simple routing logic based on the hash path
  let Component = Home;
  
  // Clean the path to handle potential trailing slashes or query params if needed
  const cleanPath = path.split('?')[0];

  // Keep per-route SEO meta tags in sync for static pages (dynamic pages set
  // their own richer meta). Safe to call unconditionally before any early return.
  useSeo(cleanPath);

  // Meta PageView on client-side route changes (initial view is sent by the
  // pixel itself). No-op unless the visitor consented.
  const firstPathRef = React.useRef(true);
  useEffect(() => {
    // Der Pixel eines Veranstalters gilt nur auf dessen Event-/Checkout-Seiten.
    if (!cleanPath.startsWith('/event/') && cleanPath !== '/checkout') clearPartnerPixel();
    if (firstPathRef.current) { firstPathRef.current = false; return; }
    if (hasConsent()) trackMeta('PageView');
  }, [cleanPath]);

  // Dashboards (No Navbar/Footer in layout as they have their own)
  let isDashboard = false;
  if (cleanPath.startsWith('/dashboard')) {
    isDashboard = true;
    // Use startsWith to ensure specific matches and prevent overlaps (e.g. /fleet/drivers vs /driver)
    if (cleanPath.startsWith('/dashboard/club')) Component = ClubDashboard;
    else if (cleanPath.startsWith('/dashboard/organizer')) Component = OrganizerDashboard;
    else if (cleanPath.startsWith('/dashboard/bar')) Component = BarDashboard;
    else if (cleanPath.startsWith('/dashboard/driver')) Component = DriverDashboard;
    else if (cleanPath.startsWith('/dashboard/fleet')) Component = FleetDashboard;
    else if (cleanPath.startsWith('/dashboard/master')) Component = MasterDashboard;
    else if (cleanPath.startsWith('/dashboard/customer')) Component = CustomerDashboard;
    else if (cleanPath.startsWith('/dashboard/admin')) Component = FleetDashboard; // Fallback/Legacy
  }

  // Homepage alias: support both / and /home so direct loads and redirects stay consistent.
  if (cleanPath === '/home') Component = Home;

  // Marketing Pages
  if (cleanPath === '/features') Component = FeaturesPage;
  if (cleanPath === '/pulse') Component = PulsePage;
  if (cleanPath === '/vision') Component = VisionPage;
  if (cleanPath === '/partner') Component = PartnerPage;
  if (cleanPath === '/cities') Component = CitiesPage;
  if (cleanPath === '/careers') Component = CareersPage;
  if (cleanPath === '/events') Component = EventsPage;
  if (cleanPath.startsWith('/event/')) Component = EventDetailPage;
  if (cleanPath.startsWith('/deal/')) Component = DealDetailPage;
  if (cleanPath === '/checkout') Component = CheckoutPage;
  if (cleanPath.startsWith('/profile/')) Component = ProfilePage;
  if (cleanPath === '/organizer-info' || cleanPath === '/veranstalter') Component = OrganizerInfoPage;
  if (cleanPath === '/bar-model' || cleanPath === '/barmodell') Component = BarModelPage;
  if (cleanPath === '/campaigns' || cleanPath === '/kampagnen') Component = CampaignsPage;
  if (cleanPath === '/faq') Component = FAQPage;
  if (cleanPath === '/rechtliches') Component = LegalOverview;
  if (cleanPath === '/impressum') Component = Impressum;
  if (cleanPath === '/datenschutz') Component = Datenschutz;
  if (cleanPath === '/agb') Component = AGB;
  if (cleanPath === '/jugendschutz') Component = Jugendschutz;
  if (cleanPath === '/widerruf') Component = Widerruf;
  if (cleanPath === '/haftung') Component = Haftung;
  if (cleanPath === '/contact') Component = ContactPage;
  if (cleanPath === '/account/tickets') Component = AccountTickets;
  if (cleanPath === '/account/profile') Component = AccountProfile;

  const routeFallback = (
    <div className="min-h-screen flex items-center justify-center bg-black text-white">
      <div className="text-center px-6">
        <div className="mx-auto mb-4 h-10 w-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
        <div className="text-sm text-gray-400">Lädt…</div>
      </div>
    </div>
  );
  // Stripe onboarding return handler: when Stripe redirects to /account/organizer/stripe
  if (cleanPath === '/account/organizer/stripe') {
    const StripeReturn: React.FC = () => {
      useEffect(() => {
        try {
          const qs = new URLSearchParams(window.location.search);
          const success = qs.get('success');
          const refresh = qs.get('refresh');
          if (success === '1' || refresh === '1') {
            try {
              if (window.opener && typeof window.opener.postMessage === 'function') {
                window.opener.postMessage({ type: 'stripe:onboard', success: success === '1' }, '*');
              }
            } catch (e) { /* ignore */ }
            // try to close popup after a short delay so the message is delivered
            setTimeout(() => { try { window.close(); } catch (e) {} }, 400);
          }
        } catch (e) { /* ignore */ }
      }, []);
      return (
        <div className="min-h-screen flex items-center justify-center bg-black text-white">
          <div className="text-center p-6 bg-night-900/80 rounded-xl border border-white/5">
            <h3 className="text-xl font-bold mb-2">Stripe Onboarding</h3>
            <p className="text-sm">Wenn der Onboarding‑Flow abgeschlossen ist, schließe dieses Fenster oder kehre zur App zurück.</p>
            <div className="mt-4"><a href="/" className="text-blue-400 underline">Zurück zur App</a></div>
          </div>
        </div>
      );
    };
    Component = StripeReturn;
  }
  
  // Standalone pages
  if (cleanPath === '/download') {
      return (
        <div className="bg-black min-h-screen text-white selection:bg-purple-500 selection:text-white font-sans">
          <Navbar />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Lädt…</div>}>
           <DownloadPage />
          </Suspense>
        </div>
      );
  }

  if (cleanPath === '/debug-file-input') {
    return <DebugFileInputContent />;
  }

  if (cleanPath === '/driver-login') {
    return (
      <div className="bg-black min-h-screen text-white font-sans">
        <Navbar />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Lädt…</div>}>
          <DriverLogin />
        </Suspense>
      </div>
    );
  }

  if (cleanPath === '/organizer-login') {
    return (
      <div className="bg-black min-h-screen text-white font-sans">
        <Navbar />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Lädt…</div>}>
          <OrganizerLogin />
        </Suspense>
      </div>
    );
  }

  if (cleanPath === '/bar-login') {
    return (
      <div className="bg-black min-h-screen text-white font-sans">
        <Navbar />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Lädt…</div>}>
          <BarLogin />
        </Suspense>
      </div>
    );
  }

  if (cleanPath === '/club-login') {
    return (
      <div className="bg-black min-h-screen text-white font-sans">
        <Navbar />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Lädt…</div>}>
          <ClubLogin />
        </Suspense>
      </div>
    );
  }

  if (cleanPath === '/fleet-login') {
    return (
      <div className="bg-black min-h-screen text-white font-sans">
        <Navbar />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Lädt…</div>}>
          <FleetLogin />
        </Suspense>
      </div>
    );
  }

  if (cleanPath === '/master-login') {
     return (
       <div className="bg-black min-h-screen text-white font-sans">
         <Navbar />
         <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Lädt…</div>}>
           <MasterLogin />
         </Suspense>
       </div>
     );
   }

  if (cleanPath === '/login') {
    return (
      <div className="bg-black min-h-screen text-white font-sans">
        <Navbar />
        <RouteErrorBoundary resetKey={cleanPath}>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Lädt…</div>}>
            <GuestLogin />
          </Suspense>
        </RouteErrorBoundary>
      </div>
    );
  }

  if (cleanPath === '/register') {
    return (
      <div className="bg-black min-h-screen text-white font-sans">
        <Navbar />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Lädt…</div>}>
          <GuestRegister />
        </Suspense>
      </div>
    );
  }

  if (cleanPath === '/reset-password') {
    return (
      <div className="bg-black min-h-screen text-white font-sans">
        <Navbar />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Lädt…</div>}>
          <ResetPassword />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen text-white selection:bg-purple-500 selection:text-white font-sans overflow-x-hidden">
      {/* Debug overlay removed */}
            {dummyTemplateOpen && (
              <div className="fixed inset-0 z-[10000] flex items-center justify-center">
                <div onClick={() => document.dispatchEvent(new CustomEvent('closeDummyTemplatePage'))} className="absolute inset-0 bg-black/80" />
                <div className="relative z-50 max-w-2xl w-full bg-white text-black rounded-2xl p-8 shadow-xl">
                  <h3 className="text-lg font-bold mb-2">Dummy: Template speichern</h3>
                  <p className="text-sm text-gray-700 mb-4">Dies ist eine Dummy-Seite — der Speichern-Flow wurde ausgelöst.</p>
                  <div className="flex justify-end">
                    <button onClick={() => document.dispatchEvent(new CustomEvent('closeDummyTemplatePage'))} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Schließen</button>
                  </div>
                </div>
              </div>
            )}
      <PresetsModal isOpen={topPresetsOpen} onClose={() => document.dispatchEvent(new CustomEvent('closePresetsModal'))} />
      {/* If we're on a dashboard route, render only the dashboard component (no Navbar/Footer)
          but keep overlays mounted so global events can open them. */}
      {isDashboard ? (
        <DashboardGuard path={cleanPath}>
          <RouteErrorBoundary resetKey={cleanPath}>
            <Suspense fallback={routeFallback}>
              <Component />
            </Suspense>
          </RouteErrorBoundary>
        </DashboardGuard>
      ) : (
        <>
          <Navbar />
          <RouteErrorBoundary resetKey={cleanPath}>
            <Suspense fallback={routeFallback}>
              <Component />
            </Suspense>
          </RouteErrorBoundary>
          <Footer />
        </>
      )}
      <ConsentBanner />
      {globalSaveTemplateOpen && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center">
          <div onClick={() => document.dispatchEvent(new CustomEvent('closeSaveTemplateModal'))} className="absolute inset-0 bg-black/80" />
          <div className="relative z-50 max-w-2xl w-full bg-night-900 border border-white/10 rounded-2xl p-8 shadow-xl">
            <h3 className="text-xl font-bold mb-2 text-white">Template speichern</h3>
              <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 font-semibold mb-2">Template Name *</label>
                <input value={globalSaveTemplateName} onChange={e => setGlobalSaveTemplateName(e.target.value)} placeholder="z.B. Standard Club Event, Festival Setup" className="w-full bg-black/60 border border-white/6 p-3 rounded-2xl text-white placeholder-gray-500" />
              </div>
              <div>
                <div className="text-sm font-bold text-white mb-2">Folgende Kategorien werden gespeichert:</div>
                <div className="rounded-xl bg-night-800/40 border border-white/6 p-3 max-h-64 overflow-y-auto pr-3">
                  <div className="space-y-3">
                    {(globalSaveTemplateData?.categories || []).map((c: any, idx: number) => (
                      <div key={c.id || idx} className="p-4 rounded-lg bg-night-900/60 border border-white/5 flex items-center justify-between">
                        <div>
                          <div className="font-bold text-white">{c.name || '—'}</div>
                          <div className="text-xs text-gray-400 mt-1">{(c.price !== undefined && c.price !== null ? Number(c.price).toFixed(0) : '0')} € • {c.capacity === '' || Number(c.capacity) === 0 ? '∞ Tickets' : `${Number(c.capacity)} Tickets`}</div>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-green-700/90 flex items-center justify-center text-white"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4"><path fill="currentColor" d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z"/></svg></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-gray-400 mt-3">{(globalSaveTemplateData?.categories || []).length} Kategorien werden gespeichert</div>
                {(globalSaveTemplateData?.categories || []).length === 0 && (
                  <div className="text-sm text-red-400 mt-2">Zum Anlegen einer Vorlage muss mindestens eine Kategorie hinzugefügt sein.</div>
                )}
              </div>
                <div className="flex items-center gap-4 mt-6">
                <button onClick={() => document.dispatchEvent(new CustomEvent('closeSaveTemplateModal'))} className="px-6 py-3 rounded-full bg-white/5 text-gray-300 hover:bg-white/10">Abbrechen</button>
                <div className="flex-1" />
                <button
                  disabled={!globalSaveTemplateName.trim() || (globalSaveTemplateData?.categories || []).length === 0}
                  onClick={() => { document.dispatchEvent(new CustomEvent('closeSaveTemplateModal')); document.dispatchEvent(new CustomEvent('saveTemplateConfirmed', { detail: { ...globalSaveTemplateData, name: globalSaveTemplateName.trim() } })); }}
                  className={`px-6 py-3 rounded-full font-semibold ${!globalSaveTemplateName.trim() || (globalSaveTemplateData?.categories || []).length === 0 ? 'bg-black text-gray-400 cursor-not-allowed' : 'text-white bg-gradient-to-r from-purple-600 to-indigo-500 shadow-xl'}`}>
                  Template speichern
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const App: React.FC = () => {
  const isDebugFileInput = typeof window !== 'undefined' && window.location && window.location.pathname === '/debug-file-input';
  if (isDebugFileInput) {
    return (
      <RouterProvider>
        <DebugFileInputContent />
      </RouterProvider>
    );
  }
  return (
    <ServerTimeProvider>
      <DashboardProvider>
        <RouterProvider>
          <div className="overflow-x-hidden">
            <AppContent />
          </div>
        </RouterProvider>
      </DashboardProvider>
    </ServerTimeProvider>
  );
};

export default App;
